import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GripVertical, XLg } from 'react-bootstrap-icons';
import { useScreenWidth } from '../../hooks/useScreenWidth';
import useLocalization from '../../hooks/useLocalization';
import { calculateModalPosition } from '../../helpers/global';

const DraggableModal = ({
  show,
  onHide,
  hideCloseButton = false,
  title,
  width = { sm: 400, md: 600, lg: 600 },
  height,
  maxModalHeight = null,
  popupOffset = null,
  pinnedPosition = null,
  onPinnedPositionChange = null,
  children,
  HeaderExtraComponent,
  LeftHeaderExtraComponent,
  renderHeader,
  hasOverlay = false,
  reposition = false,
  overlayPointerEvents = 'auto',
  onModalShellMouseEnter,
  onModalShellMouseLeave
}) => {
  const { screenSize, screenWidth } = useScreenWidth();
  const { isRTL } = useLocalization();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const lastDragPositionRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = e => {
    setIsDragging(true);
    setInitialPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    document.body.classList.add('no-select');
  };

  const handleMouseMove = e => {
    if (isDragging) {
      modalRef.current.classList.remove('draggable-modal-content');
      const next = {
        x: e.clientX - initialPosition.x,
        y: e.clientY - initialPosition.y > 0 ? e.clientY - initialPosition.y : 0
      };
      lastDragPositionRef.current = next;
      setPosition(next);
    }
  };

  const handleMouseUp = () => {
    if (isDragging && onPinnedPositionChange) {
      onPinnedPositionChange({ ...lastDragPositionRef.current });
    }
    setIsDragging(false);
    document.body.classList.remove('no-select');
    modalRef.current?.classList.add('draggable-modal-content');
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const positionAtCenter = () => {
    const { innerWidth, innerHeight } = window;
    const { offsetWidth, offsetHeight } = modalRef.current;
    setPosition({
      x: (innerWidth - offsetWidth) / 2,
      y: (innerHeight - offsetHeight) / 2
    });
  };
  const modalWidth = useMemo(() => width[screenSize] ?? width.sm ?? 400, [width, screenSize]);

  useEffect(() => {
    if (show && !Boolean(popupOffset) && pinnedPosition == null) {
      positionAtCenter();
    }
  }, [reposition, show, popupOffset, pinnedPosition]);

  useEffect(() => {
    if (!show) return;

    const margin = 16;
    if (pinnedPosition != null && typeof pinnedPosition.x === 'number' && typeof pinnedPosition.y === 'number') {
      const maxX = Math.max(margin, window.innerWidth - modalWidth - margin);
      const maxY = Math.max(margin, window.innerHeight - margin);
      const clamped = {
        x: Math.min(Math.max(margin, pinnedPosition.x), maxX),
        y: Math.min(Math.max(margin, pinnedPosition.y), maxY)
      };
      setPosition(clamped);
      lastDragPositionRef.current = clamped;
      return;
    }

    if (Boolean(popupOffset)) {
      const modalPosition = calculateModalPosition({
        x: popupOffset?.x,
        y: popupOffset?.y,
        xOffSet: popupOffset?.xOffSet,
        yOffSet: popupOffset?.yOffSet,
        modalWidth: modalWidth,
        modalHeight: height
      });
      const { x } = modalPosition;

      if (x < 0 || x + modalWidth >= screenWidth) {
        return positionAtCenter();
      }

      setPosition(modalPosition);
    } else if (pinnedPosition == null) {
      positionAtCenter();
    }
  }, [show, pinnedPosition, popupOffset, modalWidth, height, screenSize]);

  return (
    <div
      onClick={e => {
        onHide && onHide();
      }}
      className={`draggable-modal-container ${show ? 'draggable-modal-visible' : 'draggable-modal-collapsed'}`}
      style={{
        background: hasOverlay ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
        height: hasOverlay ? '100vh' : '',
        ...(hasOverlay && overlayPointerEvents === 'none' ? { pointerEvents: 'none' } : null)
      }}
    >
      <div
        ref={modalRef}
        dir="ltr"
        className="draggable-modal draggable-modal-content border bg-white "
        style={{
          width: modalWidth,
          height: height || 'auto',
          maxHeight: maxModalHeight || undefined,
          position: 'absolute',
          top: `${position.y}px`,
          left: `${position.x}px`,
          cursor: isDragging ? 'grabbing' : 'default',
          display: maxModalHeight ? 'flex' : undefined,
          flexDirection: maxModalHeight ? 'column' : undefined,
          minHeight: 0,
          pointerEvents: hasOverlay && overlayPointerEvents === 'none' ? 'auto' : undefined
        }}
        onClick={e => e.stopPropagation()}
        onMouseEnter={onModalShellMouseEnter}
        onMouseLeave={onModalShellMouseLeave}
      >
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          style={
            maxModalHeight
              ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }
              : undefined
          }
        >
          {renderHeader ? (
            maxModalHeight ? (
              <div style={{ flexShrink: 0 }}>{renderHeader({ handleMouseDown, onHide })}</div>
            ) : (
              renderHeader({ handleMouseDown, onHide })
            )
          ) : (
            <div
              className="d-flex justify-content-between bg-gray align-items-center py-2 px-1 border-bottom mb-2"
              onMouseDown={handleMouseDown}
              style={{ cursor: 'grab', flexShrink: 0 }}
            >
              <h6 className="mb-0 d-flex align-items-center">
                {LeftHeaderExtraComponent && <LeftHeaderExtraComponent />}
                <GripVertical className="" /> <span className="mid mx-1 fw-bold">{title}</span>
              </h6>
              <div className="d-flex align-items-center">
                {HeaderExtraComponent && <HeaderExtraComponent />}
                {!hideCloseButton && <XLg onClick={onHide} className="hover" />}
              </div>
            </div>
          )}

          <div
            className={maxModalHeight ? 'flex-grow-1 min-h-0 overflow-hidden d-flex flex-column' : 'overflow-auto'}
            style={maxModalHeight ? { minHeight: 0 } : undefined}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraggableModal;
